import {
  Auth0ProviderOptions,
  CodatAuthContext,
  CodatAuthContextType,
  CodatAuthProvider,
  User,
  UserManager,
} from "@codat/client-cookie-manager";
import React, { useCallback } from "react";
import { useCookies } from "react-cookie";
import Cookies from "universal-cookie";
import isPageReloaded from "utils/isPageReloaded";
import useQueryParams from "utils/useQueryParams";

import config from "config";
import { CLIENT_ID_COOKIE_KEY, auth0Config } from "config/auth";
import oidcConfig from "config/oidcConfig";

const cookies = new Cookies();
const tokenCookie = cookies.get("mock_token");
const mock: CodatAuthContextType = {
  authType: "OIDC",
  signIn: () => {
    cookies.set("last-auth-action", "sign-in");
    return Promise.resolve();
  },
  signInPopup: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  signOutRedirect: () => Promise.resolve(),
  clearAuth0Cookies: () => Promise.resolve(),
  userManager: {
    events: {
      load: () => Promise.resolve(),
      addUserLoaded: () => Promise.resolve(),
      removeUserLoaded: () => Promise.resolve(),
      addUserUnloaded: () => Promise.resolve(),
      removeUserUnloaded: () => Promise.resolve(),
      addUserSignedIn: () => Promise.resolve(),
      removeUserSignedIn: () => Promise.resolve(),
      addUserSignedOut: () => Promise.resolve(),
      removeUserSignedOut: () => Promise.resolve(),
      addUserSessionChanged: () => Promise.resolve(),
      removeUserSessionChanged: () => Promise.resolve(),
      unload: () => Promise.resolve(),
      removeSilentRenewError: () => Promise.resolve(),
      removeAccessTokenExpiring: () => Promise.resolve(),
      removeAccessTokenExpired: () => Promise.resolve(),
      addSilentRenewError: () => Promise.resolve(),
      addAccessTokenExpiring: () => Promise.resolve(),
      addAccessTokenExpired: () => Promise.resolve(),
    },
    signinCallback: () => Promise.resolve({} as User),
    settings: {},
    createSignoutRequest: () => Promise.resolve({ url: "test.com" }),
    clearStaleState: () => Promise.resolve(),
    getUser: () => Promise.resolve({} as User),
    signinSilent: () => Promise.resolve({} as User),
    storeUser: () => Promise.resolve(),
    removeUser: () => Promise.resolve(),
    signinPopup: () => Promise.resolve({} as User),
    signinSilentCallback: () => Promise.resolve({} as User),
    stopSilentRenew: () => Promise.resolve(),
    createSigninRequest: () => Promise.resolve({ url: "test.com", state: {} }),
    processSigninResponse: (() =>
      Promise.resolve({
        access_token: "",
        /** Refresh token returned from the OIDC provider (if requested, via the
         * 'offline_access' scope) */
        refresh_token: "",
        code: "",
        error: "",
        error_description: "",
        error_uri: "",
        id_token: "",
        profile: {},
        scope: "",
        session_state: "",
        state: {},
        token_type: "",
        expired: false,
        expires_in: 0,
        isOpenIdConnect: true,
        scopes: [],
      })) as unknown as UserManager["processSigninResponse"],
    processSignoutResponse: (() =>
      Promise.resolve({})) as unknown as UserManager["processSignoutResponse"],
    metadataService: {
      resetSigningKeys: () => Promise.resolve(),
      getSigningKeys: () => Promise.resolve(),
      getMetadata: () => Promise.resolve(),
      getIssuer: () => Promise.resolve(),
      getAuthorizationEndpoint: () => Promise.resolve(),
      getUserInfoEndpoint: () => Promise.resolve(),
      getEndSessionEndpoint: () => Promise.resolve(),
      getCheckSessionIframe: () => Promise.resolve(),
      getKeysEndpoint: () => Promise.resolve(),
      getTokenEndpoint: () => Promise.resolve(),
      getRevocationEndpoint: () => Promise.resolve(),
    } as unknown as UserManager["metadataService"],
    signoutCallback: (() =>
      Promise.resolve({
        ["testUrl"]: {},
        error: "",
        error_description: "",
        error_uri: "",
        state: {},
      })) as unknown as UserManager["signoutCallback"],
    signinRedirectCallback: () => Promise.resolve({} as User),
    signinRedirect: () => Promise.resolve(),
    signoutPopup: () => Promise.resolve(),
    signoutRedirectCallback: (() =>
      Promise.resolve({
        ["testUrl"]: {},
        error: "",
        error_description: "",
        error_uri: "",
        state: {},
      })) as unknown as UserManager["signoutRedirectCallback"],
    signinPopupCallback: () => Promise.resolve({} as User),
    signoutPopupCallback: () => Promise.resolve(),
    revokeAccessToken: () => Promise.resolve(),
    querySessionStatus: () =>
      Promise.resolve({
        session_state: "",
        /** Subject identifier */
        sub: "",
        /** Session ID */
        sid: "",
      }),
    startSilentRenew: () => Promise.resolve(),
    signoutRedirect: () => Promise.resolve(),
  },
  roles: [],
  userData: {
    access_token: tokenCookie,
    id_token: tokenCookie,
    scope: "",
    session_state: "",
    state: {},
    token_type: "",
    expired: false,
    expires_in: 0,
    scopes: [],
    profile: {
      sub: "test",
      name: "test",
      email: "",
      email_verified: false,
      preferred_username: "",
      given_name: "",
      family_name: "",
      role: "",
      roles: [],
      permissions: [],
      iss: "",
      aud: "",
      iat: 0,
      exp: 0,
    },
    expires_at: 0,
    toStorageString: () => "",
  },
  isLoading: false,
  selectClient: () => {
    /* This function intentionally left blank */
  },
};

export const MockAuthProvider = ({ children }: React.PropsWithChildren) => (
  <CodatAuthContext.Provider value={mock}>{children}</CodatAuthContext.Provider>
);

const AuthProvider = ({
  children,
  onRedirectCallback,
}: React.PropsWithChildren<
  Pick<Auth0ProviderOptions, "onRedirectCallback">
>) => {
  const [, , removeCookies] = useCookies(["signin-seen"]);

  const query = useQueryParams();

  const onBeforeSignIn = useCallback(() => {
    // Copied from client-cookie-manager, quick fix to keep firstTimeClient redirect working improve later
    const existingRedirect = window.sessionStorage.getItem("redirect");
    if (!existingRedirect) {
      window.sessionStorage.setItem("redirect", window.location.href);
    }

    if (!isPageReloaded()) {
      removeCookies("signin-seen");
    }
  }, [removeCookies]);

  if (/local/.exec(window.origin) && tokenCookie !== undefined) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  } else {
    const invitationId = query["invitationId"];

    const auth0ConfigForProvider = {
      ...auth0Config,
      onRedirectCallback,
      authorizationParams: {
        ...auth0Config.authorizationParams,
        connection:
          localStorage.getItem(
            `codat-auth0-connection-${config.ENVIRONMENT.toLowerCase()}`
          ) ?? undefined,
        acr_values: typeof invitationId === "string" ? invitationId : undefined,
        "ext-invitationId":
          typeof invitationId === "string" ? invitationId : undefined,
      },
    };

    return (
      <CodatAuthProvider
        clientCookieKey={CLIENT_ID_COOKIE_KEY}
        userManagementUrl={config.USER_MANAGEMENT_URL}
        oidcConfig={{ ...oidcConfig, onBeforeSignIn }}
        auth0Config={auth0ConfigForProvider}
        isAuth0UserCookieKey={`isAuth0User-${config.ENVIRONMENT}`}
      >
        {children}
      </CodatAuthProvider>
    );
  }
};

export default AuthProvider;
